/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module NavigationModule
 */

import {
    ChangeDetectionStrategy,
    Component,
} from '@angular/core';

import { L10nService } from '@vmw/ngx-vip';
import { Store } from '@ngrx/store';
import {
    combineLatest,
    map,
    Observable,
    of,
} from 'rxjs';

import * as StateNames from 'state-names';
import * as globalL10n from 'global-l10n';
import * as SystemInfoSelectors from 'ng/root-store/system-info/system-info.selectors';
import { INavState } from '../../navigation.types';
import { NavigationService } from '../../services/navigation.service';
import * as l10n from './infrastructure-vertical-nav.component.l10n';

const { ...globalL10nKeys } = globalL10n;
const { ENGLISH: dictionary, ...l10nKeys } = l10n;

/**
 * Vertical Navigation component for the Infrastructure pages.
 * @author alextsg
 */
@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'infrastructure-vertical-nav',
    template: '<vertical-nav [states]="infrastructureStates$ | async"></vertical-nav>',
})
export class InfrastructureVerticalNavComponent {
    /**
     * List of navigation states and children that should be shown in the nav.
     */
    public infrastructureStates$: Observable<INavState[]> = of([]);

    constructor(
        navigationService: NavigationService,
        store: Store,
        l10nService: L10nService,
    ) {
        l10nService.registerSourceBundles(dictionary);

        const labelsHash = {
            [StateNames.INFRASTRUCTURE_DASHBOARD]: {
                icon: 'dashboard',
                label: l10nService.getMessage(globalL10nKeys.dashboardLabel),
            },
            [StateNames.INFRASTRUCTURE_GET_STARTED]: {
                icon: 'cursor-hand',
                label: l10nService.getMessage(l10nKeys.getStartedLabel),
            },
            [StateNames.INFRASTRUCTURE_CLOUD]: {
                icon: 'cloud',
                label: l10nService.getMessage(l10nKeys.cloudsLabel),
            },
            [StateNames.INFRASTRUCTURE_CLOUD_RESOURCES]: {
                icon: 'resource-pool',
                label: l10nService.getMessage(l10nKeys.cloudResourcesLabel),
            },
            [StateNames.CLOUD_RESOURCES_SERVICE_ENGINE]: {
                label: l10nService.getMessage(globalL10nKeys.serviceEngineLabel),
            },
            [StateNames.CLOUD_RESOURCES_SEGROUP]: {
                label: l10nService.getMessage(globalL10nKeys.serviceEngineGroupLabel),
            },
            [StateNames.CLOUD_RESOURCES_NETWORK]: {
                label: l10nService.getMessage(l10nKeys.networksLabel),
            },
            [StateNames.CLOUD_RESOURCES_VRF_CONTEXT]: {
                label: l10nService.getMessage(l10nKeys.vrfContextLabel),
            },
            [StateNames.INFRASTRUCTURE_GSLB]: {
                icon: 'network-globe',
                label: l10nService.getMessage(l10nKeys.gslbLabel),
            },
            [StateNames.GSLB_SITES]: {
                label: l10nService.getMessage(l10nKeys.sitesLabel),
            },
            [StateNames.GSLB_SUBDOMAINS]: {
                label: l10nService.getMessage(l10nKeys.subdomainsLabel),
            },
            [StateNames.GSLB_GEO_PROFILE]: {
                label: l10nService.getMessage(l10nKeys.geoProfileLabel),
            },
            [StateNames.CLOUD_RESOURECES_AVAILABILITY_ZONE]: {
                label: l10nService.getMessage(l10nKeys.availabilityZoneLabel),
            },
            [StateNames.GSLB_LEADER_CHANGE]: {
                label: l10nService.getMessage(l10nKeys.leaderChangeLabel),
            },
        };

        const states = navigationService.getChildren(StateNames.INFRASTRUCTURE)
            .map(state => {
                const { label, icon } = labelsHash[state.name];

                return {
                    ...state,
                    label,
                    icon,
                    children: state.children?.map(childState => {
                        const childStateProps = labelsHash[childState.name];

                        return {
                            ...childState,
                            label: childStateProps?.label,
                            icon: childStateProps?.icon,
                        };
                    }),
                };
            });

        this.infrastructureStates$ = combineLatest([
            store.select(SystemInfoSelectors.selectLocalSiteIsGslbLeader),
            store.select(SystemInfoSelectors.selectHasGslbConfig),
        ]).pipe(
            map(([isLeader, hasGslbConfig]) => {
                return states.map(state => {
                    if (state.name === StateNames.INFRASTRUCTURE_GSLB) {
                        return {
                            ...state,
                            children: state.children.filter(childState => {
                                switch (childState.name) {
                                    case StateNames.GSLB_SUBDOMAINS:
                                    case StateNames.GSLB_GEO_PROFILE:
                                        return isLeader;
                                    case StateNames.GSLB_LEADER_CHANGE:
                                        return hasGslbConfig;

                                    default:
                                        return true;
                                }
                            }),
                        };
                    }

                    return state;
                });
            }),
        );
    }
}
